import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import Header from '@/components/Header'
import FullPageLoader from '@/components/FullPageLoader'
import Modal from '@/components/Modal'
import Pagination from '@/components/Pagination'
import { userGrantsSelector } from '@/store/slices/user'
import {
  filterAdminConversations,
  getAdminChatBudgetPresentation,
  getAdminConversationStatusLabel,
  getAdminConversationStatusPresentation,
  getAdminQueueRequestForFilter,
  getTranscriptEmailPresentation
} from '@/store/slices/chat-inbox-state'
import {
  chatInboxSelector,
  closeAdminChatConversation,
  getAdminChatConfig,
  getAdminChatConversation,
  getAdminChatQueue,
  getChatBudget,
  openChatBudget,
  receiveAdminQueueMeta,
  receiveAdminRealtimeMessages,
  resetChatBudget,
  retryChatTranscriptEmail,
  sendAdminChatMessage,
  updateStorefrontChatEnabled
} from '@/store/slices/chat-inbox'
import { getProducts } from '@/store/slices/product'
import {
  startAdminChatRealtime,
  subscribeToAdminChatQueue,
  subscribeToAdminConversation
} from '@/firebase/chat-realtime'

const formatTime = value => value ? new Date(value).toLocaleString('en-GB') : '-'

const Attachment = ({ attachment }) => {
  if (!attachment) return null
  if (attachment.type === 'ORDER') {
    return (
      <a href={`/order/${encodeURIComponent(attachment.orderId)}`} target="_blank" rel="noopener noreferrer" className="mb-2 block rounded-xl border border-amber-200 bg-amber-50 p-3 text-left text-gray-900 no-underline hover:bg-amber-100">
        <span className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-wide text-gray-500"><span>Order attachment</span><span>{attachment.status?.replace(/_/g, ' ')}</span></span>
        <span className="mt-1 block font-semibold">#{attachment.orderId}</span>
        <span className="mt-1 block text-sm">{attachment.displayName}</span>
        <span className="mt-1 block text-xs text-gray-500">{attachment.itemCount} items · {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(attachment.totalPrice)}</span>
        <span className="mt-3 block text-xs font-semibold underline">Open order details ↗</span>
      </a>
    )
  }
  if (attachment.type === 'PRODUCT') {
    return (
      <div className="mb-2 flex gap-3 border border-gray-200 bg-white p-2">
        {attachment.imageUrl && <img src={attachment.imageUrl} alt="" className="h-12 w-12 object-cover" />}
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-gray-500">Product</div>
          <div className="truncate font-semibold">{attachment.displayName}</div>
          <div className="text-xs text-gray-500">{attachment.sku}</div>
        </div>
      </div>
    )
  }
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="mb-2 block w-full min-w-0 border border-gray-200 bg-white p-2 text-left text-gray-900 no-underline"
    >
      <span className="block text-[10px] uppercase tracking-wide text-gray-500">Link</span>
      <span className="block truncate text-sm font-semibold underline">{attachment.host || attachment.url}</span>
    </a>
  )
}

const ConversationStatusBadge = ({ status }) => {
  const presentation = getAdminConversationStatusPresentation(status)
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${presentation.badgeClassName}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${presentation.dotClassName}`} />
      {presentation.label}
    </span>
  )
}

const ChatInboxPage = () => {
  const dispatch = useDispatch()
  const grants = useSelector(userGrantsSelector)
  const {
    configLoaded,
    configAvailable,
    enabled,
    savingConfig,
    liveEnabled,
    realtimeEnabled,
    transcriptEmailEnabled,
    conversations,
    selectedConversation,
    messages,
    budget,
    counts,
    paging,
    queueLoaded,
    loading,
    sending,
    retryingTranscriptEmail
  } = useSelector(chatInboxSelector)
  const canUpdate = grants.chat?.includes('update')
  const [filter, setFilter] = useState('ALL')
  const [text, setText] = useState('')
  const [attachment, setAttachment] = useState(null)
  const [attachmentMode, setAttachmentMode] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [productResults, setProductResults] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [productLoadError, setProductLoadError] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const endRef = useRef()
  const filterRef = useRef(filter)
  const pageRef = useRef(1)
  const budgetPresentation = getAdminChatBudgetPresentation(budget?.status)

  const visibleConversations = useMemo(
    () => filterAdminConversations(conversations, filter),
    [conversations, filter]
  )

  const refreshQueue = (page = pageRef.current, selectedFilter = filterRef.current) => dispatch(getAdminChatQueue({
    ...getAdminQueueRequestForFilter(selectedFilter),
    page,
    size: paging.size || 20
  })).unwrap().catch(() => undefined)

  const selectFilter = value => {
    filterRef.current = value
    pageRef.current = 1
    setFilter(value)
    void refreshQueue(1, value)
  }

  const selectPage = page => {
    pageRef.current = page
    void refreshQueue(page)
  }

  useEffect(() => {
    let timer
    dispatch(getAdminChatConfig()).unwrap()
      .then(response => {
        if (!response.data.liveEnabled) return
        void refreshQueue(1, 'ALL')
        void dispatch(getChatBudget())
        timer = setInterval(refreshQueue, 30000)
      })
      .catch(() => toast.error('Unable to load live chat configuration'))
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    let unsubscribe
    if (!liveEnabled || !realtimeEnabled) return undefined
    startAdminChatRealtime()
      .then(connected => {
        if (connected) {
          unsubscribe = subscribeToAdminChatQueue(
            meta => {
              dispatch(receiveAdminQueueMeta(meta))
              void refreshQueue()
            },
            () => undefined
          )
        }
      })
      .catch(() => undefined)
    return () => unsubscribe?.()
  }, [dispatch, liveEnabled, realtimeEnabled])

  useEffect(() => {
    let unsubscribe
    if (selectedConversation?.id && realtimeEnabled) {
      unsubscribe = subscribeToAdminConversation(
        selectedConversation.id,
        incoming => dispatch(receiveAdminRealtimeMessages(incoming)),
        () => undefined
      )
    }
    return () => unsubscribe?.()
  }, [dispatch, realtimeEnabled, selectedConversation?.id])

  useEffect(() => {
    const emailStatus = selectedConversation?.transcriptEmail?.status
    if (!selectedConversation?.id || !['PENDING', 'SENDING'].includes(emailStatus)) return undefined
    const timer = setInterval(() => {
      void dispatch(getAdminChatConversation(selectedConversation.id))
    }, 5000)
    return () => clearInterval(timer)
  }, [dispatch, selectedConversation?.id, selectedConversation?.transcriptEmail?.status])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const selectConversation = async id => {
    setAttachment(null)
    setAttachmentMode('')
    setLinkUrl('')
    try {
      await dispatch(getAdminChatConversation(id)).unwrap()
    } catch (_) {
      toast.error('Unable to load the conversation')
    }
  }

  const sendMessage = async event => {
    event.preventDefault()
    const trimmed = text.trim()
    if ((!trimmed && !attachment) || !selectedConversation || sending) return
    const attachmentToSend = attachment
    setText('')
    setAttachment(null)
    setAttachmentMode('')
    try {
      await dispatch(sendAdminChatMessage({
        conversationId: selectedConversation.id,
        clientMessageId: crypto.randomUUID(),
        text: trimmed,
        ...(attachmentToSend && { attachment: attachmentToSend })
      })).unwrap()
      await refreshQueue()
    } catch (_) {
      setText(trimmed)
      setAttachment(attachmentToSend)
      toast.error('Unable to send the reply')
    }
  }

  const loadProducts = async search => {
    setLoadingProducts(true)
    setProductLoadError(false)
    try {
      const response = await dispatch(getProducts({
        page: 1,
        size: 5,
        status: 'PUBLISHED',
        hidden: 'false',
        search
      })).unwrap()
      setProductResults(Array.isArray(response.data)
        ? response.data.filter(product => product.status === 'PUBLISHED' && product.hidden === false).slice(0, 5)
        : [])
    } catch (_) {
      setProductResults([])
      setProductLoadError(true)
      toast.error('Unable to search products')
    } finally {
      setLoadingProducts(false)
    }
  }

  const searchProducts = event => {
    event.preventDefault()
    void loadProducts(productSearch.trim())
  }

  const openProductPicker = () => {
    setProductSearch('')
    setProductResults([])
    setAttachmentMode('PRODUCT')
    void loadProducts('')
  }

  const addLinkAttachment = event => {
    event.preventDefault()
    try {
      const url = new URL(linkUrl.trim())
      if (!['http:', 'https:'].includes(url.protocol)) throw new Error('Invalid protocol')
      setAttachment({ type: 'LINK', url: url.toString(), host: url.hostname })
      setAttachmentMode('')
      setLinkUrl('')
    } catch (_) {
      toast.error('Enter a valid http or https link')
    }
  }

  const closeConversation = async () => {
    if (!selectedConversation || !window.confirm('Close this conversation?')) return
    try {
      await dispatch(closeAdminChatConversation(selectedConversation.id)).unwrap()
      pageRef.current = 1
      await refreshQueue(1)
      toast.success('Conversation closed')
    } catch (_) {
      toast.error('Unable to close the conversation')
    }
  }

  const openBudget = async () => {
    const reason = window.prompt('Reason for pausing bot assistance and routing chats to admins:')?.trim()
    if (!reason) return
    try {
      await dispatch(openChatBudget(reason)).unwrap()
      await refreshQueue()
      toast.success('Bot assistance paused. New chats will be routed to admins.')
    } catch (_) {
      toast.error('Unable to pause bot assistance')
    }
  }

  const retryTranscriptEmail = async () => {
    if (!selectedConversation?.transcriptEmail?.manualRetryAllowed) return
    const uncertain = selectedConversation.transcriptEmail.status === 'UNKNOWN'
    const confirmation = uncertain
      ? 'Email delivery is uncertain. Retrying may send the customer a duplicate transcript. Retry anyway?'
      : 'Retry sending this transcript email?'
    if (!window.confirm(confirmation)) return
    try {
      await dispatch(retryChatTranscriptEmail(selectedConversation.id)).unwrap()
      toast.success('Transcript email queued for retry')
    } catch (_) {
      toast.error('Unable to retry the transcript email')
    }
  }

  const resetBudget = async () => {
    if (!window.confirm('Resume bot assistance for new chats? Existing chats in the admin queue will remain there.')) return
    try {
      await dispatch(resetChatBudget()).unwrap()
      toast.success('Bot assistance resumed for new chats')
    } catch (_) {
      toast.error('Unable to resume bot assistance')
    }
  }

  const toggleStorefrontChat = async () => {
    try {
      const response = await dispatch(updateStorefrontChatEnabled(!enabled)).unwrap()
      toast.success(response.data.enabled ? 'Storefront chat enabled' : 'Storefront chat disabled')
    } catch (_) {
      toast.error('Unable to update storefront chat')
    }
  }

  const storefrontChatControl = (
    <section className="mb-4 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4">
      <div>
        <div id="storefront-chat-label" className="font-bold">Storefront chat</div>
        <p id="storefront-chat-description" className="mt-1 max-w-3xl text-sm text-gray-600">
          {configAvailable
            ? enabled
              ? 'Chat is available to signed-in customers. Disable it to hide the widget and stop customer chat requests.'
              : 'Chat is hidden from the storefront and customer chat requests are blocked. Conversation history remains available here.'
            : 'Unable to load the storefront chat setting. Reload this page to try again.'}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={Boolean(enabled)}
        aria-labelledby="storefront-chat-label"
        aria-describedby="storefront-chat-description"
        disabled={!canUpdate || !configAvailable || savingConfig}
        onClick={toggleStorefrontChat}
        className="inline-flex shrink-0 items-center gap-3 rounded-full px-2 py-1 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-800 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span aria-hidden="true" className={`flex h-7 w-12 items-center rounded-full p-1 transition-colors ${enabled ? 'bg-green-600' : 'bg-gray-300'}`}>
          <span className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} />
        </span>
        {savingConfig ? 'Saving…' : configAvailable ? enabled ? 'Enabled' : 'Disabled' : 'Unavailable'}
      </button>
    </section>
  )

  if (!configLoaded) return <FullPageLoader />

  if (!liveEnabled) {
    return (
      <Header title="Live Chat">
        {storefrontChatControl}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-amber-800">
          The admin live chat inbox is currently unavailable.
        </div>
      </Header>
    )
  }

  return (
    <Header title="Live Chat">
      {storefrontChatControl}
      <section className={budgetPresentation.adminOnly
        ? 'mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-300 bg-red-50 p-4'
        : 'mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 p-4'}>
        <div className="max-w-3xl">
          <div className="font-bold">{budgetPresentation.title}</div>
          <div className="mt-1 text-sm text-gray-600">{budgetPresentation.description}</div>
          {budget?.reason && <div className="mt-2 text-sm text-gray-700"><span className="font-semibold">Reason:</span> {budget.reason}</div>}
        </div>
        {canUpdate && (
          <button
            type="button"
            className={budgetPresentation.adminOnly
              ? 'rounded-full border border-gray-800 px-4 py-2'
              : 'rounded-full bg-gray-800 px-4 py-2 text-white'}
            onClick={budgetPresentation.adminOnly ? resetBudget : openBudget}
          >
            {budgetPresentation.action}
          </button>
        )}
      </section>

      <div className="grid overflow-hidden rounded-lg border border-gray-200 bg-white lg:h-[620px] lg:grid-cols-[360px_1fr]">
        <aside className="flex min-h-0 flex-col border-b border-gray-200 lg:h-full lg:border-b-0 lg:border-r">
          <div className="flex gap-2 border-b border-gray-200 p-3">
            {['ALL', 'WAITING_ADMIN', 'LIVE', 'HISTORY'].map(value => (
              <button
                key={value}
                type="button"
                className={filter === value ? 'rounded-full bg-gray-800 px-3 py-1.5 text-xs text-white' : 'rounded-full border px-3 py-1.5 text-xs'}
                onClick={() => selectFilter(value)}
              >
                {value === 'WAITING_ADMIN'
                  ? 'Waiting'
                  : value === 'LIVE'
                    ? 'Active'
                    : value === 'HISTORY'
                      ? `Closed (${(counts.CLOSED || 0) + (counts.TIMED_OUT || 0)})`
                      : 'All'}
              </button>
            ))}
          </div>
          <div className="max-h-[300px] min-h-0 overflow-y-auto lg:max-h-none lg:flex-1">
            {filter === 'HISTORY' && (
              <div className="border-b border-gray-100 px-4 py-2 text-xs text-gray-500">
                {(counts.CLOSED || 0) + (counts.TIMED_OUT || 0)} total closed chats · {counts.CLOSED || 0} closed · {counts.TIMED_OUT || 0} timed out
              </div>
            )}
            {loading && !queueLoaded && <div className="p-6 text-center text-sm text-gray-500">Loading conversations…</div>}
            {!loading && !visibleConversations.length && <div className="p-6 text-center text-sm text-gray-500">No conversations</div>}
            {visibleConversations.map(conversation => (
              <button
                type="button"
                key={conversation.id}
                className={`w-full border-b border-gray-100 p-4 text-left ${selectedConversation?.id === conversation.id ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                onClick={() => selectConversation(conversation.id)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold">{conversation.customerLabel || 'Customer'}</span>
                  {!!conversation.unreadCount && <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs text-white">{conversation.unreadCount}</span>}
                </div>
                <div className="mt-1 text-xs text-gray-500">{getAdminConversationStatusLabel(conversation.status)}</div>
                <div className="mt-2 truncate text-sm text-gray-600">{conversation.latestMessage?.text || 'No messages'}</div>
                <div className="mt-1 text-xs text-gray-400">{formatTime(conversation.lastMessageAt)}</div>
              </button>
            ))}
          </div>
          {paging.totalPage > 1 && (
            <div className="flex justify-center border-t border-gray-200 p-2 text-xs">
              <Pagination
                currentPage={paging.page}
                totalPage={paging.totalPage}
                setPage={selectPage}
              />
            </div>
          )}
        </aside>

        <section className="flex h-[500px] min-h-0 flex-col lg:h-full">
          {!selectedConversation
            ? <div className="m-auto text-gray-500">Select a conversation</div>
            : (
              <>
                <header className="flex items-center justify-between border-b border-gray-200 p-4">
                  <div className="min-w-0">
                    <div className="truncate font-bold">{selectedConversation.customerName || 'Customer'}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="break-all text-xs text-gray-500">
                        {selectedConversation.customerId || selectedConversation.customerLabel}
                      </span>
                      <ConversationStatusBadge status={selectedConversation.status} />
                    </div>
                    {selectedConversation.transcriptEmail && (() => {
                      const emailPresentation = getTranscriptEmailPresentation(selectedConversation.transcriptEmail.status)
                      return emailPresentation && (
                        <div className={`mt-2 text-xs font-medium ${emailPresentation.className}`}>
                          {emailPresentation.label}
                          {selectedConversation.transcriptEmail.sentAt && ` · ${formatTime(selectedConversation.transcriptEmail.sentAt)}`}
                        </div>
                      )
                    })()}
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {canUpdate && transcriptEmailEnabled && selectedConversation.transcriptEmail?.manualRetryAllowed && (
                      <button
                        type="button"
                        className="rounded-full border border-violet-500 px-4 py-2 text-sm text-violet-700 disabled:opacity-40"
                        disabled={retryingTranscriptEmail}
                        onClick={retryTranscriptEmail}
                      >
                        {retryingTranscriptEmail ? 'Queuing…' : 'Retry transcript email'}
                      </button>
                    )}
                    {canUpdate && selectedConversation.active && (
                      <button type="button" className="rounded-full border border-red-500 px-4 py-2 text-sm text-red-600" onClick={closeConversation}>Close</button>
                    )}
                  </div>
                </header>
                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-gray-50 p-4">
                  {messages.map(message => (
                    <article
                      key={message.id}
                      className={message.sender === 'ADMIN'
                        ? 'ml-auto max-w-[85%] rounded-lg bg-gray-800 p-3 text-white'
                        : 'max-w-[85%] rounded-lg border border-gray-200 bg-white p-3'}
                    >
                      <div className="mb-1 text-xs font-semibold opacity-70">
                        {message.sender === 'ADMIN' ? message.adminName || 'Admin' : message.sender === 'CUSTOMER' ? 'Customer' : message.sender === 'BOT' ? 'Bot' : 'System'}
                      </div>
                      <Attachment attachment={message.attachment} />
                      <div className="whitespace-pre-wrap break-words text-sm">{message.text}</div>
                      <div className="mt-2 text-[10px] opacity-60">{formatTime(message.createdAt)}</div>
                    </article>
                  ))}
                  <div ref={endRef} />
                </div>
                <Modal
                  visibleState={[attachmentMode === 'PRODUCT', visible => setAttachmentMode(visible ? 'PRODUCT' : '')]}
                  width="w-[92vw] max-w-4xl"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-bold">Attach a product</h2>
                      <p className="text-sm text-gray-500">Choose a published product to share in this conversation.</p>
                    </div>
                    <button type="button" aria-label="Close product picker" className="rounded-lg border px-3 py-2 text-sm" onClick={() => setAttachmentMode('')}>
                      Close
                    </button>
                  </div>
                  <form className="my-4 flex gap-2" onSubmit={searchProducts}>
                    <input
                      className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      value={productSearch}
                      placeholder="Search published products"
                      disabled={!canUpdate || !selectedConversation.active || sending || loadingProducts}
                      onChange={event => setProductSearch(event.target.value)}
                    />
                    <button type="submit" className="rounded-lg border px-3 py-2 text-sm disabled:opacity-40" disabled={loadingProducts}>
                      {loadingProducts ? 'Searching…' : 'Search'}
                    </button>
                  </form>
                  <div className="max-h-[55vh] min-h-28 space-y-1 overflow-y-auto">
                    {loadingProducts && <p className="px-2 py-4 text-center text-sm text-gray-500">Loading products…</p>}
                    {!loadingProducts && productResults.map(product => (
                      <button
                        key={product.sku}
                        type="button"
                        className="flex w-full items-center gap-3 rounded-lg p-2 text-left hover:bg-gray-50"
                        onClick={() => {
                          setAttachment({
                            type: 'PRODUCT',
                            sku: product.sku,
                            displayName: product.displayName || product.name || product.sku,
                            ...(product.images?.[0] && { imageUrl: product.images[0] }),
                            productPath: `/p/${encodeURIComponent(product.name || product.displayName || product.sku)}/${encodeURIComponent(product.sku)}`,
                            source: 'PICKER'
                          })
                          setAttachmentMode('')
                        }}
                      >
                        {product.images?.[0] && <img src={product.images[0]} alt="" className="h-12 w-12 shrink-0 rounded object-cover" />}
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold">{product.displayName || product.name || product.sku}</span>
                          <span className="block text-xs text-gray-500">{product.sku}</span>
                        </span>
                      </button>
                    ))}
                    {!loadingProducts && productLoadError && (
                      <div className="px-2 py-4 text-center text-sm text-red-600">
                        Products could not be loaded. Check your access and try again.
                      </div>
                    )}
                    {!loadingProducts && !productLoadError && !productResults.length && <p className="px-2 py-4 text-center text-sm text-gray-500">No visible published products found. Try another search.</p>}
                  </div>
                </Modal>
                {attachmentMode === 'LINK' && (
                  <form className="flex gap-2 border-t border-gray-200 bg-white p-3" onSubmit={addLinkAttachment}>
                    <input
                      type="url"
                      className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      value={linkUrl}
                      placeholder="https://example.com"
                      disabled={!canUpdate || !selectedConversation.active || sending}
                      onChange={event => setLinkUrl(event.target.value)}
                    />
                    <button type="submit" className="rounded-lg border px-3 py-2 text-sm" disabled={!linkUrl.trim()}>Attach link</button>
                    <button type="button" className="rounded-lg border px-3 py-2 text-sm" onClick={() => setAttachmentMode('')}>Cancel</button>
                  </form>
                )}
                <form className="border-t border-gray-200 p-3" onSubmit={sendMessage}>
                  {attachment && (
                    <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                      <span className="min-w-0 truncate">
                        {attachment.type === 'PRODUCT' ? `Product · ${attachment.displayName}` : `Link · ${attachment.host}`}
                      </span>
                      <button type="button" className="shrink-0 text-xs text-red-600" onClick={() => setAttachment(null)}>Remove</button>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <input
                      className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2"
                      value={text}
                      maxLength={1000}
                      disabled={!canUpdate || !selectedConversation.active || sending}
                      placeholder="Reply to customer (optional with an attachment)"
                      onChange={event => setText(event.target.value)}
                    />
                    <button type="button" className="rounded-lg border px-3 py-2 text-sm disabled:opacity-40" disabled={!canUpdate || !selectedConversation.active || sending} onClick={openProductPicker}>
                      Product
                    </button>
                    <button type="button" className="rounded-lg border px-3 py-2 text-sm disabled:opacity-40" disabled={!canUpdate || !selectedConversation.active || sending} onClick={() => setAttachmentMode(attachmentMode === 'LINK' ? '' : 'LINK')}>
                      Link
                    </button>
                    <button
                      type="submit"
                      className="rounded-lg bg-gray-800 px-5 py-2 text-white disabled:opacity-40"
                      disabled={!canUpdate || (!text.trim() && !attachment) || !selectedConversation.active || sending}
                    >
                      {sending ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </form>
              </>
            )}
        </section>
      </div>
    </Header>
  )
}

export default ChatInboxPage
